import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { normalizeMeasurementChartsPayload } from "../converters/chartData";
import { getAdditionalLongitudinalProfileData } from "../services/api";
import { MILLISECONDS_PER_WEEK } from "../config/constants";
import { checkRangeLimits, getMinimalRangeLimits } from "../utils/range";
import type { LegacyLineDataset, MeasurementChartModel, MeasurementGroupChartModel } from "../types";
import { ensureArray, getValueOrDefault, isNullOrUndefined } from "../utils/common";
import CacheInfo from "./CacheInfo";
import ChainageChart from "./ChainageChart";
import type MeasurementChart from "./MeasurementChart";
import MeasurementChartContainer from "./MeasurementChartContainer";
import RangeInput, { type RangeInputRef } from "./RangeInput";

const chartsMemoryStore = new Map<string, MeasurementChartModel[]>();

type Props = {
  model: MeasurementGroupChartModel;
};

function getDatasetHiddenDates(dataset: LegacyLineDataset): string[] {
  if (!dataset.hidden) {
    return [];
  }

  const date = dataset.data?.[0]?.metadata?.measurementDate;
  return typeof date === "string" ? [date] : [];
}

function hasRenderableCharts(charts: MeasurementChartModel[]): boolean {
  return charts.length > 0 && !!charts[0]?.data && !!charts[0]?.options;
}

function isForceRefreshRequested(): boolean {
  const currentUrl = new URL(window.location.href);
  const rawValue = currentUrl.searchParams.get("forceRefresh");
  if (!rawValue) {
    return false;
  }

  return rawValue === "1" || rawValue.toLowerCase() === "true";
}

function stripOneTimeForceRefreshFromUrl(): void {
  const currentUrl = new URL(window.location.href);
  if (!currentUrl.searchParams.has("forceRefresh")) {
    return;
  }

  currentUrl.searchParams.delete("forceRefresh");
  window.history.replaceState({}, document.title, `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
}

function appendQueryParams(url: string, queryParams: Record<string, string | null | undefined>): string {
  const targetUrl = new URL(url, window.location.origin);
  Object.keys(queryParams).forEach((key) => {
    const value = queryParams[key];
    if (value === undefined || value === null || value === "") {
      return;
    }

    targetUrl.searchParams.set(key, value);
  });

  return `${targetUrl.pathname}${targetUrl.search}`;
}

export default function MeasurementChartGroup({ model }: Props) {
  const initialCharts = useMemo(
    () => normalizeMeasurementChartsPayload(model.charts) ?? [],
    [model.charts]
  );
  const [hiddenPoints, setHiddenPoints] = useState<number[]>([]);
  const [hiddenDatasets, setHiddenDatasets] = useState<string[]>([]);
  const [rangeInstances, setRangeInstances] = useState<RangeInputRef[]>([]);
  const [chartInstances, setChartInstances] = useState<MeasurementChart[]>([]);
  const [charts, setCharts] = useState<MeasurementChartModel[]>(initialCharts);
  const [chartsLoading, setChartsLoading] = useState<boolean>(Boolean(model.loadChartsAsync && !hasRenderableCharts(initialCharts)));
  const [chartsLoadError, setChartsLoadError] = useState<string | null>(null);

  const [globalMin, setGlobalMin] = useState<number | null>(null);
  const [globalMax, setGlobalMax] = useState<number | null>(null);

  const globalRangeRef = useRef<RangeInputRef | null>(null);
  const longitudinalProfileChartRef = useRef<MeasurementChart | null>(null);
  const longitudinalLoadPromise = useRef<Promise<void>>(Promise.resolve());

  const firstChart = charts[0];

  useEffect(() => {
    if (!model.loadChartsAsync || hasRenderableCharts(initialCharts) || !model.chartsDataUrl) {
      stripOneTimeForceRefreshFromUrl();
      return;
    }

    const forceRefresh = isForceRefreshRequested();
    const requestUrl = appendQueryParams(model.chartsDataUrl, {
      forceRefresh: forceRefresh ? "true" : null
    });
    const storeKey = `${model.profileId}|${requestUrl}`;

    if (!forceRefresh && chartsMemoryStore.has(storeKey)) {
      setCharts(chartsMemoryStore.get(storeKey) ?? []);
      setChartsLoading(false);
      setChartsLoadError(null);
      stripOneTimeForceRefreshFromUrl();
      return;
    }

    setChartsLoading(true);
    setChartsLoadError(null);
    fetch(requestUrl, {
      credentials: "same-origin"
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const payload = await response.json() as unknown;
        const loadedCharts = normalizeMeasurementChartsPayload(payload);
        if (loadedCharts === null) {
          throw new Error("Unsupported charts response shape");
        }

        chartsMemoryStore.set(storeKey, loadedCharts);
        setCharts(loadedCharts);
        setChartsLoading(false);
      })
      .catch((error: unknown) => {
        console.error("Charts loading failed.", error);
        setChartsLoading(false);
        setChartsLoadError("Nepodařilo se načíst grafy.");
      })
      .finally(() => {
        stripOneTimeForceRefreshFromUrl();
      });
  }, [initialCharts, model.chartsDataUrl, model.loadChartsAsync, model.profileId]);

  useEffect(() => {
    if (!firstChart) {
      return;
    }

    const scaling = firstChart.data.scalingSettings ?? {};
    const min = moment(getValueOrDefault(scaling.dataXAxisMinPos, scaling.dataXAxisMin)).startOf("day").valueOf();
    const max = moment(getValueOrDefault(scaling.dataXAxisMaxPos, scaling.dataXAxisMax)).startOf("day").valueOf();

    setGlobalMin(min);
    setGlobalMax(max);
  }, [firstChart]);

  useEffect(() => {
    if (!globalRangeRef.current || globalMin == null || globalMax == null) {
      return;
    }

    updateFromToInUrl(moment(globalMin), moment(globalMax));
  }, [globalMax, globalMin]);

  useEffect(() => {
    const exportDetailLink = document.querySelector<HTMLAnchorElement>("a#exportDetail");
    const exportLink = document.querySelector<HTMLButtonElement>("button#export");

    if (!exportDetailLink) {
      return;
    }

    const exportDetailHref = exportDetailLink.getAttribute("href") ?? "";
    const exportDetailUrl = exportDetailHref.split("?")[0];
    const exportDetailQuery = exportDetailHref.split("?")[1] ?? "";
    const preserved = exportDetailQuery
      .split("&")
      .filter((item) => item && !item.includes("excludedPointIds") && !item.includes("excludedDatasets"));

    const urlParams = new URLSearchParams();
    urlParams.set("excludedPointIds", hiddenPoints.join());
    urlParams.set("excludedDatasets", hiddenDatasets.join());

    const nextHref = `${exportDetailUrl}?${urlParams.toString()}${preserved.length > 0 ? `&${preserved.join("&")}` : ""}`;
    exportDetailLink.setAttribute("href", nextHref);
    if (exportLink) {
      exportLink.setAttribute("href", nextHref);
    }
  }, [hiddenDatasets, hiddenPoints]);

  useEffect(() => {
    const instance = chartInstances.find((item) => item.props.isLongitudinalProfileChart === true) ?? null;
    longitudinalProfileChartRef.current = instance;

    if (!instance) {
      return;
    }

    const hidden = instance.props.data.dataSets?.flatMap(getDatasetHiddenDates) ?? [];
    if (hidden.length > 0) {
      setHiddenDatasets((prev) => Array.from(new Set([...prev, ...hidden])));
    }
  }, [chartInstances]);

  const registerRangeInstance = (instance: RangeInputRef) => {
    setRangeInstances((prev) => (prev.includes(instance) ? prev : [...prev, instance]));
  };

  const registerChartInstance = (instance: MeasurementChart) => {
    setChartInstances((prev) => (prev.includes(instance) ? prev : [...prev, instance]));
  };

  const registerGlobalRangeInstance = (instance: RangeInputRef) => {
    globalRangeRef.current = instance;
  };

  const getHiddenPointIds = () => hiddenPoints;
  const getHiddenDatasets = () => hiddenDatasets;

  const onChainagePlotVisibilityChanged = (label: string, hidden: boolean) => {
    if (!label) {
      return;
    }

    setHiddenDatasets((prev) => {
      const next = new Set(prev);
      if (hidden) {
        next.add(label);
      } else {
        next.delete(label);
      }
      return Array.from(next);
    });
  };

  const onPlotVisibilityChanged = (
    _event: unknown,
    item: { text?: string; datasetIndex?: number },
    _legend: unknown
  ) => {
    const datasetName = item.text;
    if (!datasetName) {
      return;
    }

    const nextHiddenPoints = new Set(hiddenPoints);
    const nextHiddenDatasets = new Set(hiddenDatasets);

    chartInstances.forEach((instance) => {
      const chart = instance.getChartInstance();
      if (!chart) {
        return;
      }

      const datasets = chart.data.datasets as LegacyLineDataset[];
      const datasetIndex = datasets.findIndex((dataset) => dataset.label === datasetName);
      if (datasetIndex === -1) {
        return;
      }

      chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));

      const dataset = datasets[datasetIndex];
      const isHidden = !chart.isDatasetVisible(datasetIndex);

      if (instance.props.isLongitudinalProfileChart) {
        const measurementDate = dataset.data?.[0]?.metadata?.measurementDate;
        if (!isNullOrUndefined(measurementDate) && typeof measurementDate === "string") {
          if (isHidden) {
            nextHiddenDatasets.add(measurementDate);
          } else {
            nextHiddenDatasets.delete(measurementDate);
          }
        }
      } else {
        const pointId = dataset.data?.length ? dataset.data[0].pointId : null;
        if (!isNullOrUndefined(pointId) && typeof pointId === "number") {
          if (isHidden) {
            nextHiddenPoints.add(pointId);
          } else {
            nextHiddenPoints.delete(pointId);
          }
        }
      }

      chart.update();
    });

    setHiddenPoints(Array.from(nextHiddenPoints));
    setHiddenDatasets(Array.from(nextHiddenDatasets));
  };

  const updateFromToInUrl = (from: moment.Moment, to: moment.Moment) => {
    const exportDetailLink = document.querySelector<HTMLAnchorElement>("a#exportDetail");
    const exportLink = document.querySelector<HTMLButtonElement>("button#export");

    if (!exportDetailLink) {
      return;
    }

    let exportDetailUrl = exportDetailLink.getAttribute("href") ?? "";
    if (!exportDetailUrl.includes("?")) {
      exportDetailUrl += "?";
    }

    const params = new URLSearchParams(new URL(window.location.origin + exportDetailUrl).search);
    params.set("dateFrom", from.format("YYYY-MM-DD"));
    params.set("dateTo", to.format("YYYY-MM-DD"));

    const nextHref = `${exportDetailUrl.split("?")[0]}?${decodeURIComponent(params.toString())}`;
    exportDetailLink.setAttribute("href", nextHref);

    if (exportLink) {
      exportLink.setAttribute("href", nextHref);
    }
  };

  const loadLongitudinalProfileData = (fromMoment: moment.Moment, toMoment: moment.Moment) => {
    const instance = longitudinalProfileChartRef.current;
    if (!instance || isNullOrUndefined(instance.props.profileId)) {
      return;
    }

    longitudinalLoadPromise.current = longitudinalLoadPromise.current.then(async () => {
      const data = await getAdditionalLongitudinalProfileData(
        instance.props.profileId as number,
        fromMoment.format("YYYY-MM-DD"),
        toMoment.format("YYYY-MM-DD")
      );

      const chart = instance.getChartInstance();
      if (!chart || !Array.isArray(data) || data.length === 0) {
        return;
      }

      const hiddenLabels = new Set<string>();
      (chart.data.datasets as LegacyLineDataset[]).forEach((dataset, index) => {
        if (!chart.isDatasetVisible(index)) {
          hiddenLabels.add(dataset.label);
        }
      });

      chart.data.datasets = data as never;

      (chart.data.datasets as LegacyLineDataset[]).forEach((dataset, index) => {
        if (hiddenLabels.has(dataset.label)) {
          chart.setDatasetVisibility(index, false);
        }
      });

      const vertical = getMinimalRangeLimits();
      const horizontal = getMinimalRangeLimits();

      data.forEach((dataset) => {
        dataset.data.forEach((point) => {
          checkRangeLimits(vertical, Number(point.y));
          checkRangeLimits(horizontal, Number(point.x));
        });
      });

      instance.scaling.verticalAxisMax = vertical.max + 1;
      instance.scaling.verticalAxisMin = vertical.min - 1;
      instance.scaling.horizontalAxisMax = horizontal.max + 1;
      instance.scaling.horizontalAxisMin = horizontal.min - 1;

      instance.changeScaling((xScale, yScale) => {
        xScale.min = instance.scaling.horizontalAxisMin;
        xScale.max = instance.scaling.horizontalAxisMax;
        yScale.min = instance.scaling.verticalAxisMin;
        yScale.max = instance.scaling.verticalAxisMax;
      });

      instance.verticalRange?.setState({
        min: instance.scaling.verticalAxisMin,
        currentMin: instance.scaling.verticalAxisMin,
        max: instance.scaling.verticalAxisMax,
        currentMax: instance.scaling.verticalAxisMax
      });

      instance.horizontalRange?.setState({
        min: instance.scaling.horizontalAxisMin,
        currentMin: instance.scaling.horizontalAxisMin,
        max: instance.scaling.horizontalAxisMax,
        currentMax: instance.scaling.horizontalAxisMax
      });
    });
  };

  const refreshRanges = (minVal: number, maxVal: number) => {
    setGlobalMin(minVal);
    setGlobalMax(maxVal);

    rangeInstances.forEach((instance) => {
      if (!instance.props.valueIsDate) {
        return;
      }

      instance.setState({
        currentMin: minVal,
        currentMax: maxVal
      });

      instance.props.onMinChanged?.(minVal);
      instance.props.onMaxChanged?.(maxVal);
      instance.props.onParentDateRangeChanged?.(minVal, maxVal);
    });

    updateFromToInUrl(moment(minVal), moment(maxVal));
    loadLongitudinalProfileData(moment(minVal), moment(maxVal));
  };

  const topRange = useMemo(() => {
    if (!firstChart) {
      return null;
    }

    const xAxisScalingControl = firstChart.options.scalingControls?.xAxisScalingControl;
    const xAxes = firstChart.options.scales?.xAxes ?? [];

    const scaling = firstChart.data.scalingSettings ?? {};
    const dataXAxisMin = getValueOrDefault(scaling.dataXAxisMin, -10);
    const dataXAxisMax = getValueOrDefault(scaling.dataXAxisMax, 10);
    const dataXAxisMinPos = getValueOrDefault(scaling.dataXAxisMinPos, dataXAxisMin);
    const dataXAxisMaxPos = getValueOrDefault(scaling.dataXAxisMaxPos, dataXAxisMax);

    return {
      xAxisScalingControl,
      xAxes,
      dataXAxisMin,
      dataXAxisMax,
      dataXAxisMinPos,
      dataXAxisMaxPos
    };
  }, [firstChart]);

  if (!firstChart || !topRange) {
    if (chartsLoading) {
      return (
        <div className="text-center p-4">
          <i className="fa fa-spinner fa-spin" /> Načítám grafy...
        </div>
      );
    }

    if (chartsLoadError) {
      return <div className="alert alert-warning m-3">{chartsLoadError}</div>;
    }

    return <div className="alert alert-info m-3">Pro tento profil nejsou k dispozici žádné grafy.</div>;
  }

  return (
    <div>
      <CacheInfo
        cachedTime={firstChart.cachedTime}
        cacheEntities="ProfileGraph"
        entityId={`${model.profileId}`}
        reloadWithForceRefresh
      />

      <div className="scaling-controls">
        {!firstChart.isLongitudinalProfileChart ? (
          <RangeInput
            min={topRange.dataXAxisMin as number | string}
            max={topRange.dataXAxisMax as number | string}
            values={[
              topRange.dataXAxisMinPos as number | string,
              topRange.dataXAxisMaxPos as number | string
            ]}
            step={topRange.xAxisScalingControl?.stepSize ?? MILLISECONDS_PER_WEEK}
            valueIsDate={topRange.xAxes.some((item) => item.type === "time")}
            useDataMax={topRange.xAxisScalingControl?.useAlwaysDataMax}
            label="Ovladani vsech grafu"
            registerRangeReference={registerGlobalRangeInstance}
            onParentDateRangeChanged={refreshRanges}
          />
        ) : null}

        {model.showChainageExcavationChart && model.excavationChart ? (
          <ChainageChart
            height={model.excavationChart.height}
            excavationIds={ensureArray(model.excavationChart.excavationIds)}
            profileId={model.excavationChart.profileId}
            annotation={model.excavationChart.annotationWrapper}
            hideHeader={model.excavationChart.hideHeader}
            rangeMin={globalMin ?? undefined}
            rangeMax={globalMax ?? undefined}
            onPlotVisibilityChanged={onChainagePlotVisibilityChanged}
          />
        ) : null}
      </div>

      <ul className="p-0">
        {charts.map((chartModel, index) => (
          <MeasurementChartContainer
            key={`${chartModel.chartName}_${index}`}
            model={chartModel}
            registerRangeReference={registerRangeInstance}
            registerChartReference={registerChartInstance}
            onPlotVisibilityChanged={onPlotVisibilityChanged}
            getHiddenPointIds={getHiddenPointIds}
            getHiddenDatasets={getHiddenDatasets}
            profileId={model.profileId}
          />
        ))}
      </ul>
    </div>
  );
}
